import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { useAnchor } from "hooks";
import { Introduction2Content } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Introduction2Content;
}

const Introduction2: VFC<Props> = withErrorBoundary(
  ({
    content: {
      introduction2Title,
      introduction2Description,
      introduction2TextUnderline: {
        data: { introduction2TextUnderLine },
      },
    },
  }) => {
    useAnchor("introduction2");

    return (
      <StyledIntroduction2 className="introduction2">
        <div className="Content Introduction2__container">
          <div className="Introduction2__titleContainer">
            <Markdown className="Introduction2__title Color_eucalyptus30 Font_40_48_600 xl:Font_32_40_600" as="h2">
              {introduction2Title}
            </Markdown>
            <Markdown className="Introduction2__mobileTitle Color_eucalyptus30 md:Font_32_40_600" as="h2">
              {introduction2Title.split("Have each").join("Have each<br>")}
            </Markdown>
            <LegacyImage {...introduction2TextUnderLine} layout="fill" className="Introduction2__image" />
          </div>
          <Markdown className="Introduction2__description Color_eucalyptus5 Font_18_26_450">
            {introduction2Description}
          </Markdown>
        </div>
      </StyledIntroduction2>
    );
  },
  {}
);

const StyledIntroduction2 = styled.section`
  background: ${({ theme }) => theme.colors.eucalyptus["80"]};
  margin-top: -64px;
  padding-top: 64px;

  .Introduction2__container {
    max-width: calc(1028px + 2 * 40px);
    display: flex;
  }

  .Introduction2__titleContainer {
    width: max-content;
    max-width: 60%;
  }

  .Introduction2__mobileTitle {
    display: none;
  }

  .Introduction2__description {
    flex: 1;
    margin-top: 14px;
    margin-left: 80px;
  }

  .Introduction2__image {
    position: relative;
    width: 264px;
    height: 43px;
    top: -10px;
    left: 90px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Introduction2__container {
      flex-direction: column;
    }

    .Introduction2__titleContainer {
      position: relative;
      max-width: 100%;

      br:not(:last-child) {
        display: none;
      }
    }

    .Introduction2__description {
      margin-left: 0;
      margin-top: 24px;
      padding-bottom: 0;
    }

    .Introduction2__image {
      position: absolute;
      width: 215px;
      height: 48px;
      top: calc(100% - 13px);
      left: 72px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Introduction2__titleContainer {
      br:not(:last-child) {
        display: unset;
      }
    }

    .Introduction2__title {
      display: none;
    }

    .Introduction2__mobileTitle {
      display: block;
    }

    .Introduction2__description {
      margin-top: 37px;
    }
  }
`;

gql`
  fragment Introduction2Content on ComponentIntroductionIntroduction2 {
    introduction2Title
    introduction2Description
    introduction2TextUnderline {
      data {
        introduction2TextUnderLine: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
  }
`;

export { Introduction2 };
export type { Props as Introduction2Props };
